import { useEffect, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import FitnessCenterOutlinedIcon from "@mui/icons-material/FitnessCenterOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { formatRelativeTime } from "./notificationDisplay";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import "./notificationsDrawer.css";

// Notification rows are visually keyed by WHO triggered the action (n.source, derived in
// notificationDisplay.js from the API's `created_by_type`), not by which app is displaying
// them — this drawer (Office App) shows both a member's own actions from the User App and a
// coach/admin's actions from the Office App side by side, and so does the User App's own
// NotificationsDrawer (mirrored there with the same two variants). `n.type` still drives the
// message text via notificationDisplay.js's toDisplayNotification — untouched, this only
// changes the icon/accent/label.
const SOURCE_VARIANT = {
  "user-app": {
    icon: FitnessCenterOutlinedIcon,
    className: "user-app",
    label: "via User App",
  },
  "office-app": {
    icon: BusinessCenterOutlinedIcon,
    className: "office-app",
    label: "via Office App",
  },
};

// Right-side slide-in drawer shared visually by both apps (same overlay/panel/animation
// convention as web_user_v2's NotificationsDrawer). Always mounted (never conditionally
// rendered) so the transform/opacity transition actually plays on open AND close — this app
// otherwise has no precedent for animated overlays, all existing modals just mount instantly.
const NotificationsDrawer = ({
  open,
  onClose,
  notifications,
  onMarkAllRead,
  onNotificationClick,
}) => {
  const touchStartX = useRef(null);
  const unreadCount = notifications.filter((n) => !n.read).length;
  // Always mounted (per the animation comment above), so the lock is gated on `open`.
  useLockBodyScroll(open);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx > 60) onClose();
  };

  return (
    <div
      className={`hf-notif-overlay${open ? " open" : ""}`}
      aria-hidden={!open}
    >
      <div
        className={`hf-notif-panel${open ? " open" : ""}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        role="dialog"
        aria-label="Obaveštenja"
      >
        <div className="hf-notif-header">
          <h2>Obaveštenja</h2>
          <div className="hf-notif-header-actions">
            {unreadCount > 0 && (
              <button type="button" className="hf-notif-markall" onClick={onMarkAllRead}>
                Označi sve
              </button>
            )}
            <button
              type="button"
              className="hf-notif-close"
              aria-label="Zatvori"
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>

        <div className="hf-notif-list">
          {notifications.length === 0 ? (
            <div className="hf-notif-empty">
              <div className="hf-notif-empty-icon">
                <NotificationsNoneOutlinedIcon />
              </div>
              <p className="hf-notif-empty-title">Nema obaveštenja</p>
              <p className="hf-notif-empty-sub">Sve je pročitano.</p>
            </div>
          ) : (
            notifications.map((n) => {
              const variant = SOURCE_VARIANT[n.source] || SOURCE_VARIANT["office-app"];
              const SourceIcon = variant.icon;
              return (
                <button
                  key={n.id}
                  type="button"
                  className={`hf-notif-row${n.read ? "" : " unread"}`}
                  style={{ borderLeftColor: `var(--hf-${variant.className}-tint)` }}
                  onClick={() => onNotificationClick(n)}
                >
                  <span className={`hf-notif-row-icon ${variant.className}`}>
                    <SourceIcon fontSize="small" />
                  </span>
                  <span className="hf-notif-row-body">
                    <span className="hf-notif-row-message">{n.message}</span>
                    <span className="hf-notif-row-meta">
                      <span className="hf-notif-row-source">{variant.label}</span>
                      <span aria-hidden="true">·</span>
                      <span className="hf-notif-row-time">{formatRelativeTime(n.timestamp)}</span>
                    </span>
                  </span>
                  {!n.read && <span className="hf-notif-row-dot" aria-hidden="true" />}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsDrawer;
